import { useEffect, useState, useCallback } from "react";
import { apiFetch } from "../context/AuthContext";
import { fmtDateTime } from "../utils/time";

const B     = "#2C1A0E";
const GREEN = "#95BF47";

export default function ShopifyPage() {
  const [status,  setStatus]  = useState(null);
  const [remote,  setRemote]  = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncLog, setSyncLog] = useState([]);

  const loadStatus = useCallback(async () => {
    const r = await apiFetch("/api/shopify/status");
    if (r.ok) setStatus(await r.json());
    const rr = await apiFetch("/api/shopify/remote-status");
    if (rr.ok) setRemote(await rr.json());
  }, []);

  useEffect(() => { loadStatus(); }, []);

  const runSync = async (endpoint = "/api/shopify/sync") => {
    setSyncing(true); setSyncLog(["⏳ Sync started, running in background…"]);
    try {
      const r = await apiFetch(endpoint, { method: "POST" });
      const d = await r.json();
      if (!d.ok && d.error) { setSyncLog([`❌ ${d.error}`]); setSyncing(false); return; }
      const poll = async () => {
        const pr = await apiFetch("/api/shopify/sync/poll");
        const pd = await pr.json();
        if (pd.log?.length) setSyncLog(pd.log);
        if (pd.status === "running") {
          setTimeout(poll, 3000);
        } else {
          setSyncing(false);
          loadStatus();
        }
      };
      setTimeout(poll, 3000);
    } catch(e) {
      setSyncLog([`❌ ${e.message}`]); setSyncing(false);
    }
  };

  const configured = status?.configured;
  const hasCreds   = status?.has_creds;
  const connError  = status?.conn_error;

  return (
    <div style={{ padding: "28px 28px 60px", fontFamily: "Inter, sans-serif" }}>
      <h1 style={{ fontFamily:"'Cormorant Garamond',Georgia,serif",
                   color: B, fontSize: 26, fontWeight: 700, marginBottom: 4 }}>
        🛍️ Shopify Integration
      </h1>
      <p style={{ color: "#888", fontSize: 13, marginBottom: 28 }}>
        Pulls online orders, customers, products and live inventory from your Shopify store.
        Runs alongside Zoho — no overlap since they cover different customer segments.
      </p>

      <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>

        {/* ── Connection status card ── */}
        <div style={card}>
          <div style={cardTitle}>Connection</div>
          {!status ? (
            <div style={{ color: "#aaa", fontSize: 13 }}>Loading…</div>
          ) : configured ? (
            <>
              <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:16 }}>
                <span style={{ fontSize:28 }}>✅</span>
                <div>
                  <div style={{ fontWeight:700, fontSize:15, color:B }}>
                    {status.shop_info?.name || "Connected"}
                  </div>
                  <div style={{ fontSize:12, color:"#888" }}>{status.shop_info?.domain}</div>
                </div>
              </div>
              <div style={{ background:"#F7F5F2", borderRadius:7, padding:"10px 14px",
                            fontSize:12, color:"#555", marginBottom:16, lineHeight:1.9 }}>
                <div>Plan: <strong>{status.shop_info?.plan || "—"}</strong></div>
                <div>Currency: <strong>{status.shop_info?.currency || "INR"}</strong></div>
                <div>Last sync: <strong>{fmtDateTime(status.last_sync_at)}</strong></div>
                <div>Auto-sync: <strong>every 6 hours</strong></div>
                <div style={{ marginTop:4, color:"#aaa", fontSize:11 }}>
                  Tokens auto-refresh every 24h (Shopify Dev Dashboard requirement)
                </div>
              {remote?.ok && (
                <div style={{ display:"flex", gap:10, marginTop:14 }}>
                  {[["🛒","Orders",remote.orders],["👥","Customers",remote.customers],["📦","Products",remote.products]].map(([icon,label,count]) => (
                    <div key={label} style={{ flex:1, textAlign:"center", background:"#F7F5F2",
                                              borderRadius:8, padding:"10px 6px" }}>
                      <div style={{ fontSize:18 }}>{icon}</div>
                      <div style={{ fontSize:20, fontWeight:700, color:"#2C1A0E" }}>{count ?? "—"}</div>
                      <div style={{ fontSize:11, color:"#888" }}>{label}</div>
                      <div style={{ fontSize:9, color:"#bbb", marginTop:2 }}>live from Shopify</div>
                    </div>
                  ))}
                </div>
              )}
              </div>
            </>
          ) : (
            <>
              <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:16 }}>
                <span style={{ fontSize:28 }}>🔴</span>
                <div style={{ fontWeight:700, fontSize:15, color:B }}>Not Connected</div>
              </div>
              {hasCreds && connError ? (
                <div style={{ background:"#FEE2E2", border:"1px solid #FCA5A5", borderRadius:8,
                              padding:"12px 14px", fontSize:12, color:"#991B1B", lineHeight:1.7 }}>
                  <strong>Credentials found but connection failed:</strong>
                  <div style={{ fontFamily:"monospace", marginTop:6, wordBreak:"break-all" }}>
                    {connError}
                  </div>
                  <div style={{ marginTop:8, color:"#666", fontSize:11 }}>
                    Check that the app is <strong>installed on your store</strong> in the Dev Dashboard,
                    and that <code style={cs}>SHOPIFY_STORE_DOMAIN</code> is set to your{" "}
                    <code style={cs}>.myshopify.com</code> domain (not the custom domain).
                  </div>
                </div>
              ) : (
                <div style={{ background:"#FFF8E7", border:"1px solid #F0D080", borderRadius:8,
                              padding:"12px 14px", fontSize:13, color:"#7A5C00", lineHeight:1.7 }}>
                  Add <code style={cs}>SHOPIFY_CLIENT_ID</code> and{" "}
                  <code style={cs}>SHOPIFY_CLIENT_SECRET</code> to Railway env vars
                  (see setup instructions).
                </div>
              )}
            </>
          )}
        </div>

        {/* ── Setup instructions ── */}
        {!configured && (
          <div style={{ ...card, flex:2, minWidth:340 }}>
            <div style={cardTitle}>Setup Instructions</div>

            {/* Important callout */}
            <div style={{ background:"#FEF3EE", border:"1px solid #F9C49A", borderRadius:8,
                          padding:"10px 14px", fontSize:12, color:"#7A3B0E",
                          marginBottom:16, lineHeight:1.7 }}>
              ⚠️ <strong>Important change as of Jan 2026:</strong> Shopify no longer issues
              static <code style={cs}>shpat_</code> tokens from the Dev Dashboard.
              Tokens are now fetched programmatically and expire every 24 hours.
              The panel handles this automatically — you only need to provide your
              Client ID and Secret.
            </div>

            <ol style={{ paddingLeft:18, fontSize:13, lineHeight:2.4, color:"#444" }}>
              <li>
                Go to <a href="https://dev.shopify.com/dashboard" target="_blank"
                  rel="noreferrer" style={{ color:"#2F5496" }}>dev.shopify.com/dashboard</a>
              </li>
              <li>
                Click <strong>Create app</strong> → name it <em>"Embroideary Panel"</em>
                → set the App URL to <code style={cs}>https://shopify.dev/apps/default-app-home</code>
              </li>
              <li>
                In the app, go to <strong>Configuration → Admin API access scopes</strong>
                and enable:
                <div style={{ marginTop:6, display:"flex", flexWrap:"wrap", gap:6, marginBottom:4 }}>
                  {["read_orders","read_customers","read_products","read_inventory"].map(s => (
                    <code key={s} style={cs}>{s}</code>
                  ))}
                </div>
              </li>
              <li>
                Go to <strong>Settings</strong> in the app → copy your{" "}
                <strong>Client ID</strong> and <strong>Client Secret</strong>
              </li>
              <li>
                In the app, click <strong>Install → Select your store → Install</strong>
                (the app must be installed on your store for the credentials to work)
              </li>
              <li>
                Add to Railway env vars:
                <div style={{ marginTop:6, display:"flex", flexDirection:"column", gap:5 }}>
                  <code style={cs}>SHOPIFY_CLIENT_ID = your-client-id</code>
                  <code style={cs}>SHOPIFY_CLIENT_SECRET = your-client-secret</code>
                  <code style={cs}>SHOPIFY_STORE_DOMAIN = embroideary.myshopify.com</code>
                </div>
              </li>
              <li>Railway auto-redeploys — refresh this page and the status turns green ✅</li>
            </ol>
          </div>
        )}

        {/* ── Sync controls ── */}
        {configured && (
          <div style={card}>
            <div style={cardTitle}>Sync Data</div>
            <div style={{ fontSize:12, color:"#888", marginBottom:14, lineHeight:1.6 }}>
              <strong>Full sync</strong> pulls everything: customers, all-time orders,
              products and live stock levels.
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
              <button onClick={() => runSync("/api/shopify/sync")} disabled={syncing}
                style={{ ...btnPrimary, background: syncing ? "#aaa" : GREEN,
                         color:"#fff", width:"100%" }}>
                {syncing ? "⏳ Syncing…" : "🔄 Full Sync (All Data)"}
              </button>
              <div style={{ display:"flex", gap:8 }}>
                {[
                  ["🛒 Orders",    "/api/shopify/sync/orders"],
                  ["👥 Customers", "/api/shopify/sync/customers"],
                  ["📦 Products",  "/api/shopify/sync/products"],
                ].map(([label, ep]) => (
                  <button key={ep} onClick={() => runSync(ep)} disabled={syncing}
                    style={{ ...btnSecondary, flex:1 }}>{label}</button>
                ))}
              </div>
            </div>

            {syncLog.length > 0 && <SyncLog lines={syncLog} />}
            {status.last_sync_log?.length > 0 && syncLog.length === 0 && (
              <div style={{ marginTop:12 }}>
                <div style={{ fontSize:11, color:"#aaa", marginBottom:5 }}>Last sync result:</div>
                <SyncLog lines={status.last_sync_log} />
              </div>
            )}
          </div>
        )}

        {/* ── What syncs where ── */}
        <div style={{ ...card, width:"100%" }}>
          <div style={cardTitle}>What gets synced</div>
          <div style={{ display:"flex", gap:16, flexWrap:"wrap", fontSize:13 }}>
            {[
              { icon:"🛒", title:"Orders",
                desc:"All Shopify orders (any status) → Orders log. Paid orders feed Cashflow revenue actuals broken down by SKU category (Women / Men / Kids)." },
              { icon:"👥", title:"Customers",
                desc:"All Shopify customers → Customers CRM, enriched with order count and lifetime spend. Tagged source: Shopify." },
              { icon:"📦", title:"Products & Inventory",
                desc:"Active products matched to your 10 SKUs. Live stock levels from Shopify variants written to your Inventory tracker each sync." },
              { icon:"💰", title:"Cashflow",
                desc:"Shopify paid order totals combine with Zoho actuals in the financials. Each source is tracked separately (actual_amount_shopify vs actual_amount_zoho)." },
            ].map(({ icon, title, desc }) => (
              <div key={title} style={{ flex:"1 1 200px", background:"#F7F5F2",
                                        borderRadius:8, padding:"12px 14px" }}>
                <div style={{ fontSize:20, marginBottom:6 }}>{icon}</div>
                <div style={{ fontWeight:700, color:B, marginBottom:4 }}>{title}</div>
                <div style={{ color:"#666", lineHeight:1.6 }}>{desc}</div>
              </div>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
}

function SyncLog({ lines }) {
  return (
    <div style={{ marginTop:12, background:"#0D1F0D", borderRadius:8,
                  padding:"10px 14px", maxHeight:200, overflowY:"auto" }}>
      {lines.map((line, i) => (
        <div key={i} style={{
          fontSize:12, fontFamily:"monospace", marginBottom:3,
          color: line.startsWith("✅") ? "#7EC8A0"
               : line.startsWith("❌") ? "#E88"
               : line.startsWith("⚠️") ? "#F4C842" : "#ccc"
        }}>{line}</div>
      ))}
    </div>
  );
}

const card      = { background:"#fff", border:"1px solid #EDE0D4",
                    borderRadius:12, padding:"20px 22px", flex:1, minWidth:260 };
const cardTitle = { fontFamily:"'Cormorant Garamond',Georgia,serif",
                    fontSize:16, fontWeight:700, color:B, marginBottom:14 };
const cs        = { display:"inline-block", background:"#F0EBE3", padding:"2px 7px",
                    borderRadius:4, fontSize:11, fontFamily:"monospace",
                    color:"#5C3317", margin:"2px 0" };
const btnPrimary  = { border:"none", borderRadius:7, padding:"9px 18px",
                      fontSize:13, fontWeight:600, cursor:"pointer" };
const btnSecondary= { background:"#F2EDE8", color:B, border:"1px solid #DDD4C8",
                      borderRadius:7, padding:"7px 10px", fontSize:12, cursor:"pointer" };
